import momentTZ from "moment-timezone";
import { Fragment } from "react";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import moment from "moment";

import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import Hr from "components/atoms/Hr/Hr";
import { StopAddressType, StopTimeType, StopType } from "types/MultiStop";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";

const formatDateAndTime = (date: string, time: string, timezone: string) => {
  const dateTime = `${date}T${time}`;
  const formattedDate = momentTZ.tz(dateTime, timezone).format("DD MMM YYYY");
  const formattedTime = momentTZ.tz(dateTime, timezone).format("HH:mm ([UTC] Z)");
  return `${formattedDate}, ${formattedTime}`;
};

const formatDatetime = ({ datetimeStr, timezone }: { datetimeStr?: string; timezone?: string }) => {
  if (!datetimeStr || !timezone) {
    return "-";
  }
  return momentTZ(datetimeStr).tz(timezone).format("DD MMM YYYY, HH:mm (UTC Z)");
};

interface StopFormValues {
  address_reference_number?: string;

  stop_airport_name?: string;
  stop_airport_code?: string;

  stop_seaport_name?: string;
  stop_seaport_code?: string;

  stop_before_date?: string;
  stop_before_time?: string;

  stop_after_date?: string;
  stop_after_time?: string;
}

interface StopDetailValues {
  is_port_address?: boolean;
  address_details?: {
    city?: string;
    country?: string;
    custom_address_reference?: string;
    location_reference_number?: string;
    name?: string;
    state?: string;
    street_address?: string;
    street_address_line_two?: string;
    un_locode?: string;
    zipcode?: string;
  };

  airport_details?: {
    code?: string;
    name?: string;
    country_name?: string;
  };

  seaport_details?: {
    name?: string;
    un_locode?: string;
    country_name?: string;
  };
  stop_after_datetime?: string;
  stop_before_datetime?: string;
}

interface MultiStopDetailsProps {
  stops: ({
    stop_time_type?: string;
    stop_type?: string;
    stop_address_type?: string;
    stop_timezone?: string;

    stop_contact_email?: string;
    stop_contact_name?: string;
    stop_contact_number?: string;

    stop_hours_end?: string;
    stop_hours_start?: string;
    stop_notes?: string;
  } & (StopFormValues | StopDetailValues))[];
  multistop_instructions?: string;
  isQuoteRequestForm?: boolean;
}

// This component is being used at
// 1. Quote request form confirmation page for multi-stop quote requests
// 2. Quote request details page for multi-stop quote requests
export default function MultiStopDetails({
  stops,
  multistop_instructions = "",
  isQuoteRequestForm = false,
}: MultiStopDetailsProps) {
  const isPortAddressDetailRenderingEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_PORT_ADDRESS_DETAIL_RENDERING);
  if (!stops) {
    return null;
  }

  return (
    <>
      {multistop_instructions && (
        <Row className="font-b3 mb-xl">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.schedule.multistop.multistopInstructions"
              defaultMessage="Multi-stop instructions"
            />
          </Col>
          <Col>{multistop_instructions}</Col>
        </Row>
      )}
      {stops.map((stop, index) => (
        <Fragment key={index}>
          <MultiStopDetailsChild
            stop={stop}
            index={index}
            isQuoteRequestForm={isQuoteRequestForm}
            isPortAddressDetailRenderingEnabled={isPortAddressDetailRenderingEnabled}
          />
          {index < stops.length - 1 && <Hr />}
        </Fragment>
      ))}
    </>
  );
}

function MultiStopDetailsChild({
  stop,
  index,
  isQuoteRequestForm,
  isPortAddressDetailRenderingEnabled,
}: {
  stop: MultiStopDetailsProps["stops"][number];
  index: number;
  isQuoteRequestForm: boolean;
  isPortAddressDetailRenderingEnabled: boolean;
}) {
  const intl = useIntl();
  // Type assertion to combine both StopFormValues and StopDetailValues properties
  const stopData = stop as typeof stop & StopFormValues & StopDetailValues;
  const {
    address_details,
    address_reference_number,

    stop_airport_name,
    stop_airport_code,
    airport_details,

    stop_seaport_name,
    stop_seaport_code,
    seaport_details,

    stop_time_type,
    stop_after_date,
    stop_after_time,
    stop_after_datetime,

    stop_before_date,
    stop_before_time,
    stop_before_datetime,
    stop_timezone,
    stop_contact_name,
    stop_contact_number,
    stop_contact_email,

    stop_hours_start,
    stop_hours_end,
    stop_notes,
    stop_type,
    stop_address_type,
    is_port_address,
  } = stopData;

  const { data: addressV2Details } = useGetAddressesV2Details(address_reference_number!, {
    options: {
      enabled: Boolean(address_reference_number) && isQuoteRequestForm,
    },
  });

  const name = addressV2Details?.name || address_details?.name || "";
  const city = addressV2Details?.city_name || address_details?.city || "";
  const country = addressV2Details?.country_name || address_details?.country || "";
  const state = addressV2Details?.state_name || address_details?.state || "";
  const street_address = addressV2Details?.street_address || address_details?.street_address || "";
  const zipcode = addressV2Details?.zipcode || address_details?.zipcode || "";

  const cityAndPostalCode = city || zipcode ? `${city ?? ""}${city && zipcode ? ", " : " "}${zipcode ?? ""}` : "";
  const stateAndCountry = state || country ? `${state ?? ""}${state && country ? ", " : " "}${country ?? ""}` : "";

  const afterText = intl.formatMessage({ id: "general.after", defaultMessage: "after" });
  const beforeText = intl.formatMessage({ id: "general.before", defaultMessage: "before" });

  const isPickup = ["Pickup", StopType.PICKUP].includes(stop_type ?? "");
  const isAirport = ["Airport", StopAddressType.AIRPORT].includes(stop_address_type ?? "");
  const isSeaport = ["Port", StopAddressType.SEAPORT].includes(stop_address_type ?? "");
  const isAfter = ["After", StopTimeType.AFTER].includes(stop_time_type ?? "");
  const isBefore = ["Before", StopTimeType.BEFORE].includes(stop_time_type ?? "");
  const isBetween = ["Between", StopTimeType.BETWEEN].includes(stop_time_type ?? "");
  const isASAPDelivery = ["ASAP", StopTimeType.ASAP_DELIVERY].includes(stop_time_type ?? "");
  const isPortAddress = isPortAddressDetailRenderingEnabled && !!is_port_address;

  const afterDateTime = isQuoteRequestForm
    ? formatDateAndTime(stop_after_date!, stop_after_time!, stop_timezone!)
    : formatDatetime({ datetimeStr: stop_after_datetime, timezone: stop_timezone });
  const beforeDateTime = isQuoteRequestForm
    ? formatDateAndTime(stop_before_date!, stop_before_time!, stop_timezone!)
    : formatDatetime({ datetimeStr: stop_before_datetime, timezone: stop_timezone });

  const formattedDateTime = (
    <div className="text-capitalize">
      {isAfter && (
        <>
          {afterText} {afterDateTime}
        </>
      )}
      {isBefore && (
        <>
          {beforeText} {beforeDateTime}
        </>
      )}
      {isBetween && (
        <>
          {afterDateTime} - {beforeDateTime}
        </>
      )}
      {isASAPDelivery && (
        <FormattedMessage id="new-request.schedule.defaultCargoReadyDeliveryOption.asap" defaultMessage="ASAP Delivery" />
      )}
    </div>
  );

  const specifiedHours =
    stop_hours_start && stop_hours_end
      ? `${moment(stop_hours_start, "HH:mm").format("HH:mm")} - ${moment(stop_hours_end, "HH:mm").format("HH:mm")}`
      : "";

  const data = [
    {
      label: <FormattedMessage id="quote-request-detail.scheduledTime" defaultMessage="Scheduled time" />,
      value: <>{formattedDateTime}</>,
    },
    ...(!isPortAddress
      ? [
          {
            label: <FormattedMessage id="quote-request-detail.company" defaultMessage="Company" />,
            value: name,
          },
        ]
      : []),
    ...(isAirport
      ? [
          {
            label: <FormattedMessage id="general.airport" defaultMessage="Airport" />,
            value: airport_details
              ? `${airport_details.name} (${airport_details.code})`
              : `${stop_airport_name} (${stop_airport_code})`,
          },
          ...(isPortAddress
            ? [
                {
                  label: <FormattedMessage id="quote-request-detail.airportCountry" defaultMessage="Airport country" />,
                  value: airport_details?.country_name,
                },
              ]
            : []),
        ]
      : []),
    ...(isSeaport
      ? [
          {
            label: <FormattedMessage id="general.seaport" defaultMessage="Seaport" />,
            value: seaport_details
              ? `${seaport_details.name} (${seaport_details.un_locode})`
              : `${stop_seaport_name} (${stop_seaport_code})`,
          },
          ...(isPortAddress
            ? [
                {
                  label: <FormattedMessage id="quote-request-detail.seaportCountry" defaultMessage="Seaport country" />,
                  value: seaport_details?.country_name,
                },
              ]
            : []),
        ]
      : []),
    ...(!isPortAddress
      ? [
          {
            label: <FormattedMessage id="quote-request-detail.address" defaultMessage="Address" />,
            value: street_address,
          },
          {
            label: <FormattedMessage id="quote-request-detail.cityAndPostalCode" defaultMessage="City and postal code" />,
            value: cityAndPostalCode,
          },
          {
            label: <FormattedMessage id="quote-request-detail.stateAndCountry" defaultMessage="State and country" />,
            value: stateAndCountry,
          },
        ]
      : []),
    {
      label: <FormattedMessage id="quote-request-detail.contactName" defaultMessage="Contact name" />,
      value: stop_contact_name,
    },
    {
      label: <FormattedMessage id="quote-request-detail.contactNumber" defaultMessage="Contact number" />,
      value: stop_contact_number,
    },
    {
      label: <FormattedMessage id="quote-request-detail.contactEmail" defaultMessage="Contact email" />,
      value: stop_contact_email,
    },
    ...(specifiedHours
      ? [
          {
            label: isPickup ? (
              <FormattedMessage
                id="new-request.quote-confirmation.schedule.specifyPickupHour"
                defaultMessage="Specify pickup hour"
              />
            ) : (
              <FormattedMessage
                id="new-request.quote-confirmation.schedule.specifyDeliveryHour"
                defaultMessage="Specify delivery hour"
              />
            ),
            value: specifiedHours,
          },
        ]
      : []),
    ...(stop_notes
      ? [
          {
            label: isPickup ? (
              <FormattedMessage id="quote-request-detail.pickupNotes" defaultMessage="Pickup notes" />
            ) : (
              <FormattedMessage id="quote-request-detail.deliveryNotes" defaultMessage="Delivery notes" />
            ),
            value: stop_notes,
          },
        ]
      : []),
  ];
  return (
    <>
      <div className="font-medium mb-lg" style={{ color: "#4C5B71" }}>
        <FormattedMessage
          id="new-request.schedule.multistop.stopNumber"
          defaultMessage="Stop #{stopNumber}"
          values={{ stopNumber: index + 1 }}
        />
        {" - "}
        {isPickup ? (
          <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
        ) : (
          <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
        )}
      </div>
      {data.map((item, index) => (
        <Fragment key={index}>
          <Row className="font-b3 mb-2">
            <Col className="text-secondary">{item.label}</Col>
            <Col>{item.value && item.value ? item.value : "-"}</Col>
          </Row>
        </Fragment>
      ))}
    </>
  );
}

MultiStopDetails.defaultProps = {
  multistop_instructions: "",
  isQuoteRequestForm: false,
};
